import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Avatar } from '@mui/material';
import type { PriceLevel } from '../../types/opportunity';
import { getExchangeLogo } from '../../utils/exchangeUrls';
import { formatPrice, formatVolume, formatTotalValue } from '../../utils/formatters';

interface OrderbookTooltipProps {
  levels: PriceLevel[];
  side: 'buy' | 'sell';
  quote: string;
  exchange: number;
  exchangeName: string;
  tradingPair: string;
}

export const OrderbookTooltip: React.FC<OrderbookTooltipProps> = ({ 
  levels, 
  side, 
  quote, 
  exchange, 
  exchangeName, 
  tradingPair 
}) => {
  const { t } = useTranslation();
  const [logoError, setLogoError] = useState(false);
  const color = side === 'buy' ? '#00ff88' : '#ff6b6b';
  const title = side === 'buy' ? t('orderbook.buyOrderbook') : t('orderbook.sellOrderbook');
  
  // Calculate cumulative volume
  let cumulativeVolume = 0;
  const levelsWithCumulative = levels.map(level => {
    cumulativeVolume += level.volume;
    return {
      ...level,
      cumulative: cumulativeVolume
    };
  });

  return (
    <Box sx={{ p: 1, minWidth: 280, maxWidth: 400, width: 'fit-content' }}>
      {/* Header with exchange info */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        {!logoError ? (
          <Avatar
            src={getExchangeLogo(exchange)}
            sx={{ width: 24, height: 24 }}
            onError={() => setLogoError(true)}
          >
            {exchangeName[0]}
          </Avatar>
        ) : (
          <Typography variant="caption" sx={{ minWidth: 24, textAlign: 'center' }}>
            {exchangeName.substring(0, 3)}
          </Typography>
        )}
        <Box>
          <Typography 
            variant="caption" 
            sx={{ 
              color, 
              fontWeight: 600,
              display: 'block',
              fontSize: '0.85rem'
            }}
          >
            {title}
          </Typography>
          <Typography 
            variant="caption" 
            sx={{ 
              color: 'rgba(255,255,255,0.7)', 
              fontSize: '0.7rem'
            }}
          >
            {tradingPair} • {levels.length} levels
          </Typography>
        </Box>
      </Box>
      
      {/* Header */}
      <Box 
        sx={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 0.8fr 0.8fr 1fr',
          gap: 0.5,
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          pb: 0.5,
          mb: 0.5
        }}
      >
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.65rem' }}>
          {t('table.headers.price')}
        </Typography>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.65rem', textAlign: 'right' }}>
          {t('table.headers.volume')}
        </Typography>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.65rem', textAlign: 'right' }}>
          {t('orderbook.total')}
        </Typography>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.65rem', textAlign: 'right' }}>
          {t('orderbook.value')}
        </Typography>
      </Box>
      
      {/* Levels */}
      <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
        {levelsWithCumulative.map((level, index) => {
          const totalValue = level.price * level.volume;
          return (
            <Box 
              key={index}
              sx={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 0.8fr 0.8fr 1fr',
                gap: 0.5,
                py: 0.25,
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.05)'
                }
              }}
            >
              <Typography 
                variant="caption" 
                sx={{ 
                  fontSize: '0.65rem',
                  color: index === 0 ? color : 'rgba(255,255,255,0.8)',
                  fontWeight: index === 0 ? 600 : 400
                }}
              >
                {formatPrice(level.price, quote)}
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  fontSize: '0.65rem',
                  color: 'rgba(255,255,255,0.7)',
                  textAlign: 'right'
                }}
              >
                {formatVolume(level.volume)}
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  fontSize: '0.65rem',
                  color: 'rgba(255,255,255,0.6)',
                  textAlign: 'right'
                }}
              >
                {formatVolume(level.cumulative)}
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  fontSize: '0.65rem',
                  color: 'rgba(255,255,255,0.5)',
                  textAlign: 'right'
                }}
              >
                {formatTotalValue(totalValue, false, quote)}
              </Typography>
            </Box>
          );
        })}
      </Box>
      
      {/* Summary */}
      <Box 
        sx={{ 
          borderTop: '1px solid rgba(255,255,255,0.1)',
          mt: 1,
          pt: 1
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.65rem' }}>
            {t('orderbook.totalVolume')}:
          </Typography>
          <Typography variant="caption" sx={{ color, fontSize: '0.7rem', fontWeight: 600 }}>
            {formatVolume(cumulativeVolume)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.65rem' }}>
            {t('orderbook.totalValue')}:
          </Typography>
          <Typography variant="caption" sx={{ color, fontSize: '0.7rem', fontWeight: 600 }}>
            {formatTotalValue(levelsWithCumulative.reduce((sum, level) => sum + (level.price * level.volume), 0), true, quote)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};